import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Target, Link2, FileText, Loader2, CheckCircle, AlertCircle,
  TrendingUp, ScanLine, Briefcase, XCircle,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { useAuth } from '../context/AuthContext';
import { getCvList, getJobDetailBySlug, checkFitScoreByCv } from '../services/api';
import './FitAnalysisPage.css';

// isBulsun ilan linkinden slug çıkarır: .../ilan/<slug>
function extractSlug(url) {
  const match = String(url).trim().match(/\/ilan\/([^/?#\s]+)/i);
  return match ? decodeURIComponent(match[1]) : null;
}

function isValidUrl(value) {
  const v = String(value).trim();
  if (!v) return false;
  try {
    const u = new URL(v.startsWith('http') ? v : `https://${v}`);
    return Boolean(u.hostname && u.hostname.includes('.'));
  } catch {
    return false;
  }
}

const scoreColor = (score) => {
  if (score >= 75) return '#00C9A7';
  if (score >= 50) return '#F59E0B';
  return '#EF4444';
};

const FitAnalysisPage = () => {
  const { user } = useAuth();

  const [jobUrl, setJobUrl] = useState('');
  const [resolvedJob, setResolvedJob] = useState(null);
  const [resolving, setResolving] = useState(false);

  const [cvList, setCvList] = useState([]);
  const [cvLoading, setCvLoading] = useState(true);
  const [selectedCvId, setSelectedCvId] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Kayıtlı CV'ler
  useEffect(() => {
    if (!user?.candidateId) {
      setCvLoading(false);
      return;
    }
    getCvList(user.candidateId)
      .then((res) => {
        const list = (res.status && res.data) ? res.data : [];
        setCvList(list);
        if (list.length > 0) setSelectedCvId(String(list[0].id));
      })
      .catch(() => setCvList([]))
      .finally(() => setCvLoading(false));
  }, [user?.candidateId]);

  // İlan linki isBulsun ilanıysa başlığı çözüp kullanıcıya doğrulat
  const resolveJob = useCallback((url) => {
    const slug = extractSlug(url);
    if (!slug) {
      setResolvedJob(null);
      return;
    }
    setResolving(true);
    getJobDetailBySlug(slug)
      .then((job) => setResolvedJob(job && job.id ? job : null))
      .catch(() => setResolvedJob(null))
      .finally(() => setResolving(false));
  }, []);

  useEffect(() => {
    if (!jobUrl.trim()) {
      setResolvedJob(null);
      return;
    }
    const t = setTimeout(() => resolveJob(jobUrl), 600);
    return () => clearTimeout(t);
  }, [jobUrl, resolveJob]);

  const canAnalyze = isValidUrl(jobUrl) && selectedCvId && !loading;

  const handleAnalyze = async () => {
    if (!canAnalyze) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await checkFitScoreByCv({
        cvId: selectedCvId,
        jobId: resolvedJob?.id || null,
        jobUrl: resolvedJob ? null : jobUrl.trim(),
      });
      setResult(data);
    } catch (e) {
      setError(e.code === 'NOT_IMPLEMENTED' ? 'pending' : 'failed');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
  };

  const overall = result?.score ?? result?.matchScore ?? 0;
  const atsScore = result?.atsScore ?? result?.ats ?? null;
  const jobScore = result?.jobFitScore ?? result?.jobScore ?? null;
  const gaps = result?.gaps || result?.weaknesses || result?.missing || [];
  const missingKeywords = result?.missingKeywords || result?.keywords || [];

  return (
    <div className="fit-page">
      <div className="fit__container">
        <BackButton />

        <div className="fit__header">
          <div className="fit__header-icon">
            <Target size={26} />
          </div>
          <h1 className="fit__title">İlan Uyum Analizi</h1>
          <p className="fit__subtitle">
            İlan linkini yapıştır, kayıtlı CV'ni seç — ne kadar uyumlu olduğunu gör.
          </p>
        </div>

        {!result && (
          <div className="fit__form-card">
            {/* İlan linki */}
            <label className="fit__label" htmlFor="fit-job-url">
              <Link2 size={15} /> İlan Linki
            </label>
            <input
              id="fit-job-url"
              type="url"
              className="fit__input"
              placeholder="https://isbulsun.com/ilan/..."
              value={jobUrl}
              onChange={(e) => setJobUrl(e.target.value)}
              autoComplete="off"
            />

            {resolving && (
              <p className="fit__hint">
                <Loader2 size={13} className="fit__spinner" /> İlan bulunuyor...
              </p>
            )}
            {!resolving && resolvedJob && (
              <p className="fit__hint fit__hint--ok">
                <CheckCircle size={13} /> {resolvedJob.title}
                {resolvedJob.companyName ? ` — ${resolvedJob.companyName}` : ''}
              </p>
            )}
            {!resolving && !resolvedJob && jobUrl.trim() && (
              <p className="fit__hint">
                {isValidUrl(jobUrl)
                  ? 'Harici ilan linki — içerik analiz sırasında okunacak.'
                  : 'Geçerli bir ilan linki yapıştırın.'}
              </p>
            )}

            {/* CV seçimi */}
            <label className="fit__label fit__label--spaced" htmlFor="fit-cv">
              <FileText size={15} /> CV Seç
            </label>

            {cvLoading ? (
              <p className="fit__hint">
                <Loader2 size={13} className="fit__spinner" /> CV'lerin yükleniyor...
              </p>
            ) : cvList.length === 0 ? (
              <div className="fit__empty">
                <p>Henüz kayıtlı CV'niz yok.</p>
                <Link to="/panel/cv-olustur" className="fit__empty-btn">CV Oluştur</Link>
              </div>
            ) : (
              <div className="fit__select-wrap">
                <select
                  id="fit-cv"
                  className="fit__select"
                  value={selectedCvId}
                  onChange={(e) => setSelectedCvId(e.target.value)}
                >
                  {cvList.map((cv) => (
                    <option key={cv.id} value={cv.id}>
                      {cv.name}{cv.title ? ` — ${cv.title}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button className="fit__submit" onClick={handleAnalyze} disabled={!canAnalyze}>
              {loading ? (
                <><Loader2 size={16} className="fit__spinner" /> Analiz ediliyor...</>
              ) : (
                <><Target size={16} /> Uyumu Analiz Et</>
              )}
            </button>

            {error && (
              <div className={`fit__error ${error === 'pending' ? 'fit__error--pending' : ''}`}>
                <AlertCircle size={16} />
                <span>
                  {error === 'pending'
                    ? 'Uyum analizi servisi hazırlanıyor, çok yakında aktif olacak.'
                    : 'Analiz yapılamadı. Lütfen tekrar deneyin.'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Sonuç */}
        {result && (
          <div className="fit__result">
            <div className="fit__score-card">
              <div
                className="fit__score-circle"
                style={{ borderColor: scoreColor(overall), color: scoreColor(overall) }}
              >
                <span className="fit__score-value">{overall}</span>
                <span className="fit__score-label">puan</span>
              </div>
              <div className="fit__score-text">
                <h2 className="fit__score-title">Genel Uyum</h2>
                {(result.summary || result.message) && (
                  <p className="fit__score-summary">{result.summary || result.message}</p>
                )}
              </div>
            </div>

            {(atsScore !== null || jobScore !== null) && (
              <div className="fit__subscores">
                {atsScore !== null && (
                  <div className="fit__subscore">
                    <div className="fit__subscore-head">
                      <ScanLine size={16} />
                      <span>ATS Uygunluğu</span>
                    </div>
                    <div className="fit__bar">
                      <div
                        className="fit__bar-fill"
                        style={{ width: `${Math.min(100, atsScore)}%`, background: scoreColor(atsScore) }}
                      />
                    </div>
                    <span className="fit__subscore-value">{atsScore}/100</span>
                  </div>
                )}
                {jobScore !== null && (
                  <div className="fit__subscore">
                    <div className="fit__subscore-head">
                      <Briefcase size={16} />
                      <span>İlana Uygunluk</span>
                    </div>
                    <div className="fit__bar">
                      <div
                        className="fit__bar-fill"
                        style={{ width: `${Math.min(100, jobScore)}%`, background: scoreColor(jobScore) }}
                      />
                    </div>
                    <span className="fit__subscore-value">{jobScore}/100</span>
                  </div>
                )}
              </div>
            )}

            {result.strengths?.length > 0 && (
              <div className="fit__section">
                <h3 className="fit__section-title fit__section-title--green">
                  <CheckCircle size={15} /> Güçlü Yönlerin
                </h3>
                <ul className="fit__list">
                  {result.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            {gaps.length > 0 && (
              <div className="fit__section">
                <h3 className="fit__section-title fit__section-title--orange">
                  <XCircle size={15} /> Eksik / Uyumsuz Yönler
                </h3>
                <ul className="fit__list">
                  {gaps.map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              </div>
            )}

            {missingKeywords.length > 0 && (
              <div className="fit__section">
                <h3 className="fit__section-title fit__section-title--orange">
                  <ScanLine size={15} /> CV'nde Geçmeyen Anahtar Kelimeler
                </h3>
                <div className="fit__keywords">
                  {missingKeywords.map((k, i) => <span key={i} className="fit__keyword">{k}</span>)}
                </div>
              </div>
            )}

            {result.suggestions?.length > 0 && (
              <div className="fit__section">
                <h3 className="fit__section-title fit__section-title--blue">
                  <TrendingUp size={15} /> Öneriler
                </h3>
                <ul className="fit__list">
                  {result.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            <div className="fit__actions">
              <button className="fit__retry" onClick={reset}>Yeni Analiz</button>
              <Link to="/panel/cv-olustur" className="fit__cv-link">CV'ni Düzenle</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FitAnalysisPage;
